import {
  type BadgeType,
  DropdownMenu,
  Toolbar,
} from '@octopus-do/octopus-storybook'
import {
  AngleRight,
  ArrowLeft,
  ArrowUpRight,
  ChartSimpleAi,
  LanguageAi,
  Palette,
  Plug,
  Sparkle,
  Sparkles,
  SparklesLight,
} from '@octopus-do/octopus-storybook/icons'
import type React from 'react'
import { forwardRef, useState } from 'react'

import { PLAN_PRO } from '@/constants/userSubscriptionPlans'
import { useAiGenerating } from '@/hooks/selectors/useAiGenerating'
import { createSwitch, getBadgeType } from '@/utils'
import { MENU_OFFSET } from '../../constants'
import {
  AI_MENU_ID,
  type AiMenuView,
  ASSISTANT_ID,
  BACK_ID,
  CHANGE_COLORS_ID,
  DOCS_URL,
  GENERATE_ID,
  LANGUAGES,
  SEO_TAGS_ID,
  TRANSLATE_ID,
  type TranslationLanguage,
  UPGRADE_ID,
} from './constants'
import { useAiMenu } from './hooks'

// Without a badge the permission has no upsell; PRO is the plan that sells it.
const DEFAULT_PLAN = PLAN_PRO

const isLanguage = (key: string): key is TranslationLanguage =>
  LANGUAGES.some((language) => language === key)

// Back stays in the menu: the list takes the place of the view again.
const BackItem: React.FC = () => (
  <>
    <DropdownMenu.Button
      icon={ArrowLeft}
      id={BACK_ID}
      preventClose
      title="Back"
    />
    <DropdownMenu.Divider />
  </>
)

interface ColorsViewProps {
  placeholder: string
}

const ColorsView: React.FC<ColorsViewProps> = ({ placeholder }) => (
  <>
    <BackItem />
    <DropdownMenu.Prompt
      placeholder={placeholder}
      submitLabel="Generate"
      variant="purple"
    />
  </>
)

const TranslateView: React.FC = () => (
  <>
    <BackItem />
    {LANGUAGES.map((language) => (
      <DropdownMenu.Button
        icon={Sparkle}
        id={language}
        key={language}
        title={language}
      />
    ))}
  </>
)

interface ContentProps {
  colorsBadge: BadgeType
  colorsLocked: boolean
  disabled: boolean
  placeholder: string
  seoMode: boolean
  view: AiMenuView
}

// In SEO mode the menu keeps the two things the assistant can do there; the
// rest of it edits the sitemap, which SEO mode does not do.
const SeoContent: React.FC<ContentProps> = ({ disabled }) => (
  <>
    <DropdownMenu.Button
      disabled={disabled}
      icon={ChartSimpleAi}
      id={SEO_TAGS_ID}
      title="Generate SEO tags"
    />
    <DropdownMenu.Button
      disabled={disabled}
      icon={Sparkles}
      id={ASSISTANT_ID}
      title="AI assistant"
    />
  </>
)

// Change colors and Translate keep the menu open and swap the list for their
// view (figma 86165:427525 shows the list; the views follow the app menus).
const ListContent: React.FC<ContentProps> = ({
  colorsBadge,
  colorsLocked,
  disabled,
}) => (
  <>
    <DropdownMenu.Button
      disabled={disabled}
      icon={Sparkles}
      id={GENERATE_ID}
      title="AI Generate"
    />
    <DropdownMenu.Divider />
    {colorsLocked ? (
      <DropdownMenu.BadgedButton
        icon={Palette}
        id={UPGRADE_ID}
        title="Change colors"
        type={colorsBadge}
      />
    ) : (
      <DropdownMenu.Button
        disabled={disabled}
        icon={Palette}
        id={CHANGE_COLORS_ID}
        preventClose
        title="Change colors"
      >
        <AngleRight />
      </DropdownMenu.Button>
    )}
    <DropdownMenu.Button
      disabled={disabled}
      icon={LanguageAi}
      id={TRANSLATE_ID}
      preventClose
      title="Translate to..."
    >
      <AngleRight />
    </DropdownMenu.Button>
    <DropdownMenu.Button
      disabled={disabled}
      icon={Sparkles}
      id={ASSISTANT_ID}
      title="AI assistant"
    />
    <DropdownMenu.Divider />
    {disabled ? (
      <DropdownMenu.Button
        disabled
        icon={Plug}
        id={DOCS_URL}
        title="Connect AI agents"
      >
        <ArrowUpRight />
      </DropdownMenu.Button>
    ) : (
      <DropdownMenu.A href={DOCS_URL} icon={Plug} title="Connect AI agents">
        <ArrowUpRight />
      </DropdownMenu.A>
    )}
  </>
)

const Content: React.FC<ContentProps> = (props) => {
  if (props.seoMode) {
    return <SeoContent {...props} />
  }

  if (props.view === 'colors') {
    return <ColorsView placeholder={props.placeholder} />
  }

  if (props.view === 'translate') {
    return <TranslateView />
  }

  return <ListContent {...props} />
}

interface TriggerProps {
  disabled: boolean
  // The assistant is working: the button shows a loader until it is done
  loading: boolean
  onOpen: () => void
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(
  ({ disabled, loading, onOpen }, ref) => {
    const { active, toggle } = DropdownMenu.useTrigger()

    // a second click closes; opening always starts on the list
    const onClick = () => {
      if (!active) {
        onOpen()
      }
      toggle()
    }

    return (
      <Toolbar.DropdownButton
        active={active}
        disabled={disabled}
        icon={SparklesLight}
        loading={loading}
        onClick={onClick}
        ref={ref}
        tooltip="AI assistant"
        variant="ai"
      />
    )
  }
)

interface Props {
  disabled: boolean
}

export const AiMenu: React.FC<Props> = ({ disabled }) => {
  const {
    actions,
    colorsBadge,
    colorsLocked,
    disabled: frozen,
    placeholder,
    seoMode,
  } = useAiMenu()
  const aiWorking = useAiGenerating()
  const { onClick: report } = Toolbar.useToolbarClick(AI_MENU_ID)

  // The menu opens on the list every time; the views are for one visit.
  const [view, setView] = useState<AiMenuView>('list')
  const showList = () => setView('list')

  const switcher = createSwitch()
    .case([GENERATE_ID], actions.onGenerate)
    .case([CHANGE_COLORS_ID], () => setView('colors'))
    .case([TRANSLATE_ID], () => setView('translate'))
    .case([BACK_ID], showList)
    .case([ASSISTANT_ID], actions.onAssistant)
    .case([SEO_TAGS_ID], actions.onSeoTags)
    .case([UPGRADE_ID], actions.onUpgrade)
    .case([DOCS_URL], actions.onConnectAgents)

  // A view reports its pick as a bare key: the prompt text, or a language.
  const onClick = ([key]: string[]) => {
    if (view === 'colors' && key !== BACK_ID) {
      actions.onChangeColors(key)
      report([CHANGE_COLORS_ID, key])
      return
    }

    if (view === 'translate' && isLanguage(key)) {
      actions.onTranslate(key)
      report([TRANSLATE_ID, key])
      return
    }

    switcher.run([key])
    report([key])
  }

  return (
    <DropdownMenu.Root
      colorsBadge={getBadgeType(colorsBadge ?? DEFAULT_PLAN)}
      colorsLocked={colorsLocked}
      content={Content}
      disabled={frozen}
      modal={false}
      onClick={onClick}
      placeholder={placeholder}
      seoMode={seoMode}
      sideOffset={MENU_OFFSET}
      view={view}
      width="wide"
    >
      <Trigger disabled={disabled} loading={aiWorking} onOpen={showList} />
    </DropdownMenu.Root>
  )
}
